
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useSwipeable } from 'react-swipeable';
import styles from './EventDetailPage.module.css';
import { useAuth } from '../context/AuthContext';
import EventGalleryImageCard from '../components/EventGalleryImageCard';
import GalleryLightbox from '../components/GalleryLightbox';
import {
  freeRegister,
  getFreeRegisterStatus,
  createDonationCheckout,
  getMyDonations,
} from '../api/events';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '../utils/apiError';
import { Helmet } from 'react-helmet-async';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import { MAP_TILE_URL, MAP_TILE_ATTRIBUTION } from '../config/mapTile';
import pin2IconUrl from '../assets/pin2.svg';
import 'leaflet/dist/leaflet.css';

const pickerIcon = new L.Icon({
  iconUrl: pin2IconUrl,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -32],
});



const EventDetailPage = () => {
  const formatYen = (amount) => `¥${Number(amount).toLocaleString()}`;
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === '1';
  const { t } = useTranslation('eventDetail');
  const { t: tApi } = useTranslation('apiErrors');
  const [event, setEvent] = useState(null);
  const [error, setError] = useState('');
  const [applyError, setApplyError] = useState('');
  const [quantities, setQuantities] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeTab, setActiveTab] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryError, setGalleryError] = useState('');
  const [galleryExists, setGalleryExists] = useState(null); // null=未確認 true/false=確認済み
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [artistModal, setArtistModal] = useState(null);
  // 無料参加登録
  const [freeRegStatus, setFreeRegStatus] = useState(null);
  const [freeRegLoading, setFreeRegLoading] = useState(false);
  const [freeRegSubmitting, setFreeRegSubmitting] = useState(false);
  const [freeRegError, setFreeRegError] = useState('');
  // 投げ銭
  const [donationList, setDonationList] = useState([]);
  const [donationLoading, setDonationLoading] = useState(false);
  const [donationAmount, setDonationAmount] = useState('');
  const [donationSubmitting, setDonationSubmitting] = useState(false);
  const [donationError, setDonationError] = useState('');
  const [donationResult, setDonationResult] = useState(null); // 'success' | 'cancel' | null
  const navigate = useNavigate();
  const { user } = useAuth();
  const location = useLocation();
  const canceledStatuses = new Set([
    'cancellation_pending_payment',
    'canceled',
    'refunding',
    'cancellation_completed',
    'cancellation_failed',
  ]);

  // スワイプハンドラも先頭で定義
  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (event && event.flyers && event.flyers.length > 0) {
        setCurrentIndex((prev) => (prev + 1) % event.flyers.length);
      }
    },
    onSwipedRight: () => {
      if (event && event.flyers && event.flyers.length > 0) {
        setCurrentIndex((prev) => (prev - 1 + event.flyers.length) % event.flyers.length);
      }
    },
    trackMouse: true,
  });

  useEffect(() => {
    let active = true;
    const apiUrl = isPreview
      ? `${process.env.REACT_APP_API_URL}/my-events/${id}/status`
      : `${process.env.REACT_APP_API_URL}/events/${id}`;
    axios.get(apiUrl)
      .then((res) => {
        const eventData = res?.data?.event;
        const status = String(eventData?.cancellation_status || 'none');
        if (canceledStatuses.has(status)) {
          const eventPathId = eventData?.slug || id;
          navigate(`/events/${eventPathId}/cancelled`, {
            replace: true,
            state: { eventName: eventData?.name || '' },
          });
          return;
        }
        if (active) setEvent(eventData);
      })
      .catch(() => {
        if (active) setError(t('loadFailed'));
      });

    return () => {
      active = false;
    };
  }, [id, navigate]);

  const {
    name, open_time, start_time, end_time,
    venue, address, description, genre, event_artists, flyers = []
  } = event || {};

  const normalizedDescription = (() => {
    if (description === null || description === undefined) return '';
    if (typeof description !== 'string') return String(description);
    const trimmed = description.trim();
    if (trimmed.toLowerCase() === 'null') return '';
    return description;
  })();

  // 新API: price_groups に対応（旧 prices からの後方互換も維持）
  const priceGroups = Array.isArray(event?.price_groups) ? event.price_groups : null;
  // フラットな価格配列（旧API互換）
  const flatPrices = priceGroups
    ? priceGroups.flatMap(g => (g.prices || []).map(p => ({
        ...p,
        type: g.type || '',
        price_date: g.price_date || null,
        price_end_date: g.price_end_date || null,
      })))
    : (Array.isArray(event?.prices) ? event.prices : []);

  const today = new Date();

  const parseDate = (d) => {
    if (!d) return null;
    let s = String(d);
    // 支持: YYYY-MM-DD, YYYY-MM-DDTHH:mm[:ss], YYYY-MM-DD HH:mm[:ss]
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      s = `${s}T00:00:00`;
    } else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/.test(s)) {
      s = s.replace(' ', 'T');
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) s += ':00';
    }
    const t = new Date(s);
    return isNaN(t) ? null : t;
  };

  // 日付のみの場合は当日の終わり(23:59:59)で比較するためのヘルパー
  const parseDateEOD = (d) => {
    if (!d) return null;
    const s = String(d);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const t = new Date(`${s}T23:59:59`);
      return isNaN(t) ? null : t;
    }
    return parseDate(d);
  };

  const isPurchasable = (price) => {
    const from = parseDate(price.price_date);
    const to = parseDate(price.price_end_date);
    if (from && today < from) return false;
    if (to && today > to) return false;
    return true;
  };

  const priceStatusLabel = (price) => {
    const from = parseDate(price.price_date);
    const to = parseDate(price.price_end_date);
    if (from && today < from) return t('saleBefore');
    if (to && today > to) return t('saleEnded');
    return null;
  };

  const renderTypeLabel = (price) => {
    // Known types handling
    if (price.type === 'vip') return <strong>{t('typeVip')}</strong>;
    if (price.type === 'group') return <strong>{t('typeGroup', { min: price.min_people })}</strong>;

    if (price.type === '通常') {
      if (price.gender) return <strong>{price.gender === 'male' ? t('typeMale') : t('typeFemale')}</strong>;
      return <strong>{t('typeNormal')}</strong>;
    }

    // Fallbacks for free-input values: show type and/or price_type if present
    if (price.price_type) {
      return <span>{String(price.type || '').trim()} {String(price.price_type).trim()}</span>;
    }
    if (price.type) return <span>{String(price.type).trim()}</span>;
    return null;
  };

  const handleApply = () => {
    const items = Object.entries(quantities || {})
      .map(([price_id, qty]) => ({ price_id, quantity: Number(qty) }))
      .filter((i) => i.quantity > 0);
    if (!items.length) {
      setApplyError(t('applyErrorNone'));
      return;
    }
    const totalQty = items.reduce((sum, i) => sum + i.quantity, 0);
    if (totalQty > 10) {
      setApplyError(t('applyErrorMax'));
      return;
    }
    setApplyError('');
    navigate(`/events/${id}/confirm`, { state: { items } });
  };

  const handleFreeRegister = async () => {
    setFreeRegSubmitting(true);
    setFreeRegError('');
    try {
      await freeRegister(event.slug || event.public_id);
      const status = await getFreeRegisterStatus(event.slug || event.public_id);
      setFreeRegStatus(status);
    } catch (err) {
      const msg = getApiErrorMessage(err, tApi);
      setFreeRegError(msg);
    } finally {
      setFreeRegSubmitting(false);
    }
  };

  const handleDonationSubmit = async () => {
    const amt = parseInt(donationAmount, 10);
    if (!amt || amt < 500) {
      setDonationError(t('donationMinError'));
      return;
    }
    if (amt > 50000) {
      setDonationError(t('donationMaxError'));
      return;
    }
    setDonationSubmitting(true);
    setDonationError('');
    try {
      const data = await createDonationCheckout(event.slug || event.public_id, amt);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      const msg = getApiErrorMessage(err, tApi);
      setDonationError(msg);
      setDonationSubmitting(false);
    }
  };

  const getEventStartAt = () => {
    if (event?.start_at) return parseDate(event.start_at);
    const datePart = String(event?.date || '').slice(0, 10);
    const startPart = String(start_time || '').slice(0, 8);
    if (datePart && startPart) return parseDate(`${datePart} ${startPart}`);
    if (datePart) return parseDate(`${datePart}T00:00:00`);
    return null;
  };

  const eventStartAt = getEventStartAt();
  const hasEventStarted = !!eventStartAt && today >= eventStartAt;
  const eventEnd = parseDateEOD(event?.end_date || event?.date);
  const hasEventEnded = !!eventEnd && today > eventEnd;
  const ticketTabLabel = event?.event_type === 'free' ? t('tabFreeReg') : t('tabTicket');
  // 無料参加登録タブは終了済みイベントでは非表示
  const showTicketTab = event?.event_type !== 'free' || !hasEventEnded;
  const showDonationTab = !!event?.donation_enabled;
  // ギャラリータブは画像が存在する場合のみ表示
  const showGalleryTab = hasEventStarted && galleryExists === true;
  const tabs = showGalleryTab
    ? [t('tabBasic'), t('tabPerformers'), ...(showTicketTab ? [ticketTabLabel] : []), t('tabGallery'), ...(showDonationTab ? [t('tabDonation')] : [])]
    : [t('tabBasic'), t('tabPerformers'), ...(showTicketTab ? [ticketTabLabel] : []), ...(showDonationTab ? [t('tabDonation')] : [])];

  // イベントロード後、未選択なら基本情報タブを開く
  useEffect(() => {
    if (event && activeTab === null) {
      setActiveTab(t('tabBasic'));
    }
  }, [event, activeTab, t]);

  useEffect(() => {
    if (!hasEventStarted && activeTab === t('tabGallery')) {
      setActiveTab(t('tabBasic'));
    }
  }, [hasEventStarted, activeTab, t]);

  useEffect(() => {
    if (galleryExists === false && activeTab === t('tabGallery')) {
      setActiveTab(t('tabBasic'));
    }
  }, [galleryExists, activeTab, t]);

  useEffect(() => {
    if (hasEventEnded && event?.event_type === 'free' && activeTab === ticketTabLabel) {
      setActiveTab(t('tabBasic'));
    }
  }, [hasEventEnded, event?.event_type, activeTab, ticketTabLabel, t]);

  // クエリパラメータで投げ銭結果を表示
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const result = params.get('donation_result');
    if (result === 'success' || result === 'cancel') {
      setDonationResult(result);
      setActiveTab(t('tabDonation'));
      // URLをクリーン
      const cleanUrl = location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }
  }, [location.search, location.pathname, t]);

  // 無料参加状態取得
  useEffect(() => {
    if (!event || event.event_type !== 'free' || !user) return;
    let cancelled = false;
    setFreeRegLoading(true);
    getFreeRegisterStatus(event.slug || event.public_id)
      .then((data) => { if (!cancelled) setFreeRegStatus(data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setFreeRegLoading(false); });
    return () => { cancelled = true; };
  }, [event, user]);

  // 投げ銭履歴取得
  useEffect(() => {
    if (!event || !user || activeTab !== t('tabDonation')) return;
    let cancelled = false;
    setDonationLoading(true);
    getMyDonations(event.slug || event.public_id)
      .then((data) => { if (!cancelled) setDonationList(data.donations || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setDonationLoading(false); });
    return () => { cancelled = true; };
  }, [event, user, activeTab]);

  useEffect(() => {
    if (!hasEventStarted || !event) return;
    let cancelled = false;
    axios.get(`${process.env.REACT_APP_API_URL}/events/${id}/gallery`, { params: { page: 1 } })
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.data?.data) ? res.data.data : (Array.isArray(res?.data) ? res.data : []);
        setGalleryExists(list.length > 0);
        setGalleryImages(list);
      })
      .catch(() => { if (!cancelled) setGalleryExists(false); });
    return () => { cancelled = true; };
  }, [hasEventStarted, event, id]);

  useEffect(() => {
    // 事前取得済みの場合は再フェッチしない
    if (!hasEventStarted || activeTab !== t('tabGallery') || galleryImages.length > 0) return;
    let cancelled = false;
    setGalleryLoading(true);
    setGalleryError('');

    axios.get(`${process.env.REACT_APP_API_URL}/events/${id}/gallery`, {
      params: { page: 1 },
    })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data;
        const list = Array.isArray(payload?.data)
          ? payload.data
          : (Array.isArray(payload) ? payload : []);
        setGalleryImages(list);
      })
      .catch((err) => {
        if (cancelled) return;
        setGalleryImages([]);
        const status = err?.response?.status;
        setGalleryError(status === 404 ? t('galleryUnavailableDraft') : t('galleryLoadFailed'));
      })
      .finally(() => {
        if (!cancelled) setGalleryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hasEventStarted, activeTab, id]);

  const openGalleryLightbox = (index) => {
    if (!galleryImages.length) return;
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  if (error) return <p>{error}</p>;
  if (!event) return <p>{t('loading')}</p>;

  return (
    <div className={styles.container}>
      <Helmet>
        <title>{name ? `${name} | IRIE TIX` : 'IRIE TIX'}</title>
        <meta name="description" content={normalizedDescription ? normalizedDescription.slice(0, 150) : `${name} — IRIE TIX`} />
        {flyers[0]?.url && <meta property="og:image" content={flyers[0].url} />}
        <meta property="og:title" content={name ? `${name} | IRIE TIX` : 'IRIE TIX'} />
        <meta property="og:type" content="website" />
      </Helmet>
      {isPreview && (
        <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontWeight: 'bold' }}>
          {t('previewBanner')}
        </div>
      )}
      <h1 className={styles.title}>{name}</h1>

      {flyers.length > 0 ? (
        <div className={styles.slider} {...handlers}>
          <img
            src={flyers[currentIndex].url}
            alt={t('flyerAlt')}
            className={styles.flyer}
          />
          {hasEventEnded && (
            <div className={styles.endedOverlay}>
              <span className={styles.endedLabel}>{t('eventEnded')}</span>
            </div>
          )}
          {flyers.length > 1 && (
            <div className={styles.indicator}>
              {flyers.map((flyer, i) => (
                <span
                  key={flyer.id ?? i}
                  className={i === currentIndex ? styles.dotActive : styles.dot}
                  onClick={() => setCurrentIndex(i)}
                ></span>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className={styles.noImage}>No Image</div>
      )}

      {/* Tabs */}
      <div className={styles.tabs}>
        {tabs.map((tab) => (
          <button
            key={tab}
            className={`${styles.tabButton} ${activeTab === tab ? styles.activeTab : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === t('tabBasic') && (
        <div className={styles.infoSection}>

          {/* 日時 */}
          <div className={styles.infoCard}>
            <div className={styles.infoCardBody}>
              <div className={styles.infoCardLabel}>{t('date')}</div>
              <div className={styles.infoCardValue}>
                {event.date && (() => {
                  const d = event.date.slice(0, 10);
                  const e = (event.end_date || '').slice(0, 10);
                  // 終了日が翌日かつ終演が午前中 → 深夜終演とみなし終了日を非表示
                  const hideEndDate = e && d !== e && (() => {
                    const diff = (new Date(e) - new Date(d)) / 86400000;
                    const endH = parseInt((end_time || '99:00').slice(0, 2), 10);
                    return diff === 1 && endH < 12;
                  })();
                  if (!e || d === e || hideEndDate) return d;
                  return `${d} 〜 ${e}`;
                })()}
              </div>
              <div className={styles.infoCardSub}>
                {t('open')} {open_time?.slice(0, 5)}　{t('start')} {start_time?.slice(0, 5)}　{t('end')} {end_time?.slice(0, 5)}
              </div>
            </div>
          </div>

          {/* ジャンル */}
          {genre && (
            <div className={styles.infoCard}>
              <div className={styles.infoCardBody}>
                <div className={styles.infoCardLabel}>{t('genre')}</div>
                <div className={styles.infoCardValue}>{genre}</div>
              </div>
            </div>
          )}

          {/* 会場 */}
          <div className={styles.infoCard}>
            <div className={styles.infoCardBody}>
              <div className={styles.infoCardLabel}>{t('venue')}</div>
              <div className={styles.infoCardValue}>
                {event.venue_relation?.user?.slug ? (
                  <Link to={`/profile/${event.venue_relation.user.slug}`} className={styles.venueLink}>
                    {venue}
                  </Link>
                ) : venue}
              </div>
              {address && <div className={styles.infoCardSub}>{address}</div>}
              {(() => {
                const lat = event?.venue_relation?.latitude ?? event?.lat;
                const lng = event?.venue_relation?.longitude ?? event?.lng;
                const hasCoords = lat != null && lng != null;
                if (!hasCoords) return null;
                const center = [lat, lng];
                const query = encodeURIComponent(`${lat},${lng}`);
                const href = `https://www.google.com/maps/search/?api=1&query=${query}`;
                return (
                      <>
                        <div style={{ marginTop: 12 }}>
                        <MapContainer
                          center={center}
                          zoom={15}
                          scrollWheelZoom={false}
                          style={{
                          height: 220,
                          borderRadius: 8,
                          border: '1px solid #d1d5db',
                          }}
                        >
                        <TileLayer
                          url={MAP_TILE_URL}
                          attribution={MAP_TILE_ATTRIBUTION}
                        />
                        <Marker
                           position={center}
                           icon={pickerIcon}
                        />
                        </MapContainer>
                        </div>
                  <a href={href} target="_blank" rel="noopener noreferrer" className={styles.mapLink}>
                    {t('openMap')} →
                  </a>
                </>
                );
              })()}
            </div>
          </div>

          {/* チケット情報 */}
          {flatPrices.length > 0 && (
            <div className={styles.infoCard}>
              <div className={styles.infoCardBody}>
                <div className={styles.infoCardLabel}>{t('ticketInfo')}</div>
                {(() => {
                  const map = new Map();
                  flatPrices.forEach((p) => {
                    const key = `${p.type}||${p.price_date || ''}||${p.price_end_date || ''}`;
                    if (!map.has(key))
                      map.set(key, { type: p.type, price_date: p.price_date, price_end_date: p.price_end_date, items: [] });
                    map.get(key).items.push(p);
                  });
                  return Array.from(map.values()).map((g, gi) => (
                    <div key={gi} className={styles.ticketGroup}>
                      <div className={styles.ticketGroupName}>
                        {renderTypeLabel(g)}
                        {g.price_date && (
                          <span className={styles.ticketGroupPeriod}> {t('salePeriod')} {String(g.price_date || '').slice(0, 16)}{g.price_end_date ? ` 〜 ${String(g.price_end_date).slice(0, 16)}` : ''}</span>
                        )}
                      </div>
                      <div className={styles.ticketGroupItems}>
                        {g.items.map((it) => (
                          <div key={it.id} className={styles.ticketGroupItem}>
                            <span>{it.price_type}{it.min_people ? ` (${it.min_people}人〜)` : ''}</span>
                            <span className={styles.ticketGroupPrice}>¥{Number(it.price).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}

          {/* イベント説明 */}
          {normalizedDescription && (
            <div className={styles.infoCardWide}>
              <div className={styles.infoCardLabel}>{t('eventDescription')}</div>
              <div className={styles.descriptionText}>{normalizedDescription}</div>
            </div>
          )}

          {/* 問い合わせ */}
          {event?.contact_info && (
            <div className={styles.infoCardWide}>
              <div className={styles.infoCardLabel}>{t('contact')}</div>
              <div className={styles.descriptionText}>{event.contact_info}</div>
            </div>
          )}

        </div>
      )}

            {activeTab === t('tabPerformers') && (
        <div>
          <h3>{t('performers')}</h3>
          {(() => {
            const performerGroups = Array.isArray(event?.performer_groups) ? event.performer_groups : [];
            const hasGroups = performerGroups.length > 0;

            const renderArtistItem = (artist, idx) => {
              const imgUrl = artist.artist_profile_image_url || null;
              const initials = (artist.artist_name || '').trim().charAt(0).toUpperCase();
              const isCustomArtist = artist.is_registered === false;
              const hasArtistDescription = String(artist.artist_description || '').trim() !== '';
              const canOpenModal = isCustomArtist && hasArtistDescription;
              const handleArtistClick = () => { if (!canOpenModal) return; setArtistModal(artist); };
              const Name = artist.artist_slug ? (
                <Link to={`/profile/${artist.artist_slug}`} className={styles.venueLink}>{artist.artist_name}</Link>
              ) : (
                <button type="button"
                  className={canOpenModal ? styles.artistToggle : styles.artistNamePlain}
                  onClick={handleArtistClick} disabled={!canOpenModal}>
                  {artist.artist_name}
                </button>
              );
              return (
                <li key={idx}
                  className={`${styles.artistItem} ${canOpenModal ? styles.artistItemClickable : ''}`}
                  onClick={!artist.artist_slug ? handleArtistClick : undefined}>
                  <div className={styles.artistItemMain}>
                    {imgUrl
                      ? <img src={imgUrl} alt={`${artist.artist_name} avatar`} className={styles.artistAvatar} />
                      : <span className={styles.artistAvatar} aria-hidden="true">{initials || 'A'}</span>}
                    <div>
                      {Name}
                      {canOpenModal && <div className={styles.artistToggleHint}>{t('artistClickHint')}</div>}
                    </div>
                  </div>
                </li>
              );
            };

            if (!hasGroups) {
              return (
                <ul className={styles.artistList}>
                  {event_artists.map((artist, idx) => renderArtistItem(artist, idx))}
                </ul>
              );
            }

            // グループ別表示
            const grouped = performerGroups.map(g => ({
              ...g,
              artists: event_artists
                .filter(a => a.group_key === g.key)
                .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
            }));
            const ungrouped = event_artists
              .filter(a => !a.group_key)
              .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

            return (
              <div>
                {grouped.map((g) => (
                  <div key={g.key} className={styles.performerGroup}>
                    <div className={styles.performerGroupName}>{g.name}</div>
                    <hr className={styles.performerGroupDivider} />
                    <ul className={styles.artistList}>
                      {g.artists.map((artist, idx) => renderArtistItem(artist, idx))}
                    </ul>
                  </div>
                ))}
                {ungrouped.length > 0 && (
                  <ul className={styles.artistList} style={{ marginTop: grouped.length ? 24 : 0 }}>
                    {ungrouped.map((artist, idx) => renderArtistItem(artist, idx))}
                  </ul>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {activeTab === ticketTabLabel && event?.event_type === 'free' && (
        <div className={styles.purchaseForm}>
          {freeRegLoading ? (
            <p style={{ textAlign: 'center', color: '#6b7280' }}>{t('freeRegLoading')}</p>
          ) : !user ? (
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <p>{t('freeRegLoginRequired')}</p>
              <button
                className={styles.button}
                onClick={() => navigate(`/login?next=${encodeURIComponent(location.pathname)}`)}
              >
                {t('loginPage')}
              </button>
            </div>
          ) : user.role !== 'buyer' ? (
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <p>{t('freeRegRoleError')}</p>
              <p>{t('freeRegRoleErrorSub')}</p>
              <button
                className={styles.button}
                onClick={() => navigate('/register')}
              >
                {t('registerPage')}
              </button>
            </div>
          ) : freeRegStatus?.registered ? (
            <div className={styles.registeredBox}>
              <p className={styles.registeredTitle}>{t('freeRegRegistered')}</p>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <p>{t('freeRegFree')}</p>
              {freeRegError && <p className={styles.errorText}>{freeRegError}</p>}
              <button
                className={styles.freeRegButton}
                onClick={handleFreeRegister}
                disabled={freeRegSubmitting}
              >
                {freeRegSubmitting ? t('freeRegRegistering') : t('freeRegRegister')}
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === ticketTabLabel && event?.event_type !== 'free' && (
        <div className={styles.purchaseForm}>
          {(() => {
            const end = parseDateEOD(event.end_date || event.date);
            const eventUpcoming = !end || end >= today;
            if (!eventUpcoming) {
              return (
                <div style={{ textAlign: 'center', padding: '12px', color: '#6b7280' }}>
                  {t('ticketEventEnded')}
                </div>
              );
            }
            // イベント未終了時の通常フロー
            if (!user) {
              return (
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <p>{t('ticketLoginRequired')}</p>
                  <button
                    className={styles.button}
                    onClick={() => {
                      const next = `${location.pathname}${location.search || ''}`;
                      navigate(`/login?next=${encodeURIComponent(next)}`);
                    }}
                  >
                    {t('loginPage')}
                  </button>
                </div>
              );
            }
            if (user.role !== 'buyer') {
              return (
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <p>{t('ticketRoleError')}</p>
                  <p>{t('ticketRoleErrorSub')}</p>
                  <button
                    className={styles.button}
                    onClick={() => navigate('/register')}
                  >
                    {t('registerPage')}
                  </button>
                </div>
              );
            }

            const purchasable = flatPrices.filter(isPurchasable);
            if (purchasable.length === 0) {
              return (
                <div style={{ textAlign: 'center', padding: '12px', color: '#6b7280' }}>
                  {t('ticketNoAvailable')}
                </div>
              );
            }

            const map = new Map();
            purchasable.forEach((p) => {
              const key = `${p.type}||${p.price_date || ''}||${p.price_end_date || ''}`;
              if (!map.has(key)) map.set(key, { type: p.type, price_date: p.price_date, price_end_date: p.price_end_date, items: [] });
              map.get(key).items.push(p);
            });

            return (
              <div className={styles.ticketPurchaseSection}>
                {Array.from(map.values()).map((g, gi) => (
                  <div key={gi} className={styles.ticketPurchaseGroup}>
                    <div className={styles.ticketPurchaseGroupHeader}>
                      <span>{renderTypeLabel(g)}</span>
                      {g.price_date && (
                        <span className={styles.ticketPurchasePeriod}>
                          {t('salePeriod')} {String(g.price_date || '').slice(0, 16)}{g.price_end_date ? ` 〜 ${String(g.price_end_date).slice(0, 16)}` : ''}
                        </span>
                      )}
                    </div>
                    {g.items.map((price) => {
                      const statusLabel = priceStatusLabel(price);
                      const isSoldOut = !!statusLabel;
                      return (
                        <div key={price.id} className={`${styles.ticketPurchaseRow} ${isSoldOut ? styles.ticketPurchaseRowDisabled : ''}`}>
                          <div className={styles.ticketPurchaseInfo}>
                            <div className={styles.ticketPurchaseName}>
                              {price.price_type}
                              {price.min_people ? <span className={styles.ticketPurchaseSub}> ({price.min_people}{t('minPeopleUnit')})</span> : null}
                            </div>
                            <div className={styles.ticketPurchasePrice}>{formatYen(price.price)}</div>
                            {statusLabel && <div className={styles.ticketPurchaseStatus}>{statusLabel}</div>}
                          </div>
                          <div className={styles.ticketPurchaseQtyWrap}>
                            <button
                              type="button"
                              className={styles.qtyBtn}
                              disabled={isSoldOut || (quantities[price.id] ?? 0) <= 0}
                              onClick={() => setQuantities((prev) => {
                                const cur = prev[price.id] ?? 0;
                                const next = { ...prev };
                                if (cur <= 1) { delete next[price.id]; } else { next[price.id] = cur - 1; }
                                return next;
                              })}
                            >－</button>
                            <span className={styles.qtyValue}>{quantities[price.id] ?? 0}</span>
                            <button
                              type="button"
                              className={styles.qtyBtn}
                              disabled={isSoldOut || (quantities[price.id] ?? 0) >= 10}
                              onClick={() => setQuantities((prev) => ({ ...prev, [price.id]: (prev[price.id] ?? 0) + 1 }))}
                            >＋</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
                {applyError && <div className={styles.ticketApplyError}>{applyError}</div>}
                <button className={styles.ticketApplyBtn} onClick={handleApply}>{t('ticketApply')}</button>
              </div>
            );
          })()}
        </div>
      )}

      {activeTab === t('tabGallery') && hasEventStarted && (
        <div className={styles.gallerySection}>
          {galleryError && <p className={styles.galleryError}>{galleryError}</p>}
          {galleryLoading && <p className={styles.galleryLoading}>{t('galleryLoading')}</p>}
          {!galleryLoading && !galleryError && galleryImages.length === 0 && (
            <p className={styles.galleryMuted}>{t('galleryEmpty')}</p>
          )}
          {!galleryLoading && !galleryError && galleryImages.length > 0 && (
            <div className={styles.galleryGrid}>
              {galleryImages.map((img, idx) => (
                <EventGalleryImageCard
                  key={`detail-gallery-${img.id}`}
                  image={img}
                  onImageClick={() => openGalleryLightbox(idx)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === t('tabDonation') && (
        <div className={styles.donationSection}>
          {donationResult === 'success' && (
            <div className={styles.donationSuccess}>
              {t('donationSuccess')}
            </div>
          )}
          {donationResult === 'cancel' && (
            <div className={styles.donationCancel}>
              {t('donationCancel')}
            </div>
          )}

          {!event?.donation_enabled ? (
            <p style={{ color: '#6b7280' }}>{t('donationDisabled')}</p>
          ) : !event?.is_donation_open ? (
            <p style={{ color: '#6b7280' }}>{t('donationClosed')}</p>
          ) : (
            <>
              <div className={styles.donationBox}>
                <h3 className={styles.donationTitle}>{t('donationTitle')}</h3>
                <p className={styles.donationDesc}>
                  {t('donationDesc')}
                  {event?.donation_end_at && (
                    <span> {t('donationPeriod', { date: String(event.donation_end_at).slice(0, 10) })}</span>
                  )}
                </p>
                {!user ? (
                  <div style={{ textAlign: 'center' }}>
                    <p>{t('donationLoginRequired')}</p>
                    <button
                      className={styles.button}
                      onClick={() => navigate(`/login?next=${encodeURIComponent(location.pathname)}`)}
                    >
                      {t('loginPage')}
                    </button>
                  </div>
                ) : (
                  <div className={styles.donationForm}>
                    <label className={styles.donationLabel}>{t('donationLabel')}</label>
                    <div className={styles.donationAmountRow}>
                      {[500, 1000, 3000, 5000].map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          className={`${styles.presetButton} ${donationAmount === String(preset) ? styles.presetActive : ''}`}
                          onClick={() => { setDonationAmount(String(preset)); setDonationError(''); }}
                        >
                          ¥{preset.toLocaleString()}
                        </button>
                      ))}
                    </div>
                    <input
                      type="number"
                      className={styles.donationInput}
                      min="500"
                      max="50000"
                      step="100"
                      placeholder={t('donationPlaceholder')}
                      value={donationAmount}
                      onChange={(e) => { setDonationAmount(e.target.value); setDonationError(''); }}
                    />
                    {donationError && <p className={styles.errorText}>{donationError}</p>}
                    <button
                      className={styles.donationButton}
                      onClick={handleDonationSubmit}
                      disabled={donationSubmitting || !donationAmount}
                    >
                      {donationSubmitting ? t('donationSubmitting') : t('donationSubmit', { amount: Number(donationAmount || 0).toLocaleString() })}
                    </button>
                  </div>
                )}
              </div>

              {user && donationList.length > 0 && (
                <div className={styles.donationHistory}>
                  <h4 className={styles.donationHistoryTitle}>{t('donationHistoryTitle')}</h4>
                  <div className={styles.donationTableWrapper}>
                  <table className={styles.donationTable}>
                    <thead>
                      <tr>
                        <th>{t('donationColDate')}</th>
                        <th>{t('donationColAmount')}</th>
                        <th>{t('donationColStatus')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {donationList.map((d) => (
                        <tr key={d.public_id}>
                          <td>{String(d.created_at || '').slice(0, 10)}</td>
                          <td>¥{Number(d.amount).toLocaleString()}</td>
                          <td>
                            {d.status === 'paid' ? t('donationStatusPaid') :
                             d.status === 'refunded' ? t('donationStatusRefunded') : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  {donationLoading && <p style={{ color: '#6b7280' }}>{t('donationHistoryLoading')}</p>}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <GalleryLightbox
        images={galleryImages}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />

      {artistModal && (
        <div className={styles.artistModalOverlay} onClick={() => setArtistModal(null)}>
          <div className={styles.artistModalContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.artistModalClose} onClick={() => setArtistModal(null)} aria-label="close">✕</button>
            {artistModal.artist_profile_image_url ? (
              <img
                src={artistModal.artist_profile_image_url}
                alt={artistModal.artist_name}
                className={styles.artistModalImage}
              />
            ) : (
              <span className={styles.artistModalInitials}>
                {(artistModal.artist_name || '').trim().charAt(0).toUpperCase() || 'A'}
              </span>
            )}
            <h3 className={styles.artistModalName}>{artistModal.artist_name}</h3>
            {artistModal.artist_description && (
              <p className={styles.artistModalDesc}>{artistModal.artist_description}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetailPage;
